import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { InvitationDetails } from '../types/invitation.type';
import { ADMIN_INVITATIONS_API_PATHS } from '../constants/invitations-api-paths.constants';

type UseAdminGetInvitationByIdResult = {
  isLoading: boolean;
  isRefetching: boolean;
  invitation: InvitationDetails | null;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetInvitationByIdResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  invitation: null,
};

export function useAdminGetInvitationById({ invitationId }: { invitationId: string }): UseAdminGetInvitationByIdResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<InvitationDetails | undefined>(
        ADMIN_INVITATIONS_API_PATHS.GET_INVITATION_BY_ID(invitationId)
      );

      setResult({ isLoading: false, isRefetching: false, invitation: response ?? null });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, "Une erreur est survenue lors de la récupération de l'invitation"),
        { variant: 'error' }
      );
      setResult((previous) => ({ ...previous, isLoading: false, isRefetching: false }));
    }
  }, [invitationId, enqueueSnackbar]);

  useEffect(() => {
    setResult(INITIAL_STATE);
  }, [invitationId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
